import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores';
import { API_URL } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Hourglass, Zap } from 'lucide-react';
import { EventProLogo } from '../components/ui/Logo';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login, isLoading } = useAuthStore();
  
  // États pour le logo client
  const [companyName, setCompanyName] = useState("Chargement...");
  const [companyLogo, setCompanyLogo] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/settings/public-config`)
        .then(res => res.json())
        .then(data => {
            setCompanyName(data.company_name);
            setCompanyLogo(data.logo_base64);
        })
        .catch(() => setCompanyName(""));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await login(email, password);
      toast.success('Connexion réussie !');
    } catch (error) {
      if (error.status === 403 || error.response?.status === 403) {
        toast.warning(error.message || "Compte en attente de validation", {
          description: "Un administrateur doit approuver votre profil avant votre première connexion.",
          icon: <Hourglass className="w-4 h-4 text-orange-500" />,
          duration: 6000,
        });
      } else {
        toast.error(error.message || 'Email ou mot de passe incorrect');
      }
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center p-4 bg-slate-100 font-sans">
      
      {/* LOGO EVENTPRO - EN HAUT À GAUCHE */}
      <div className="absolute top-6 left-6 md:top-8 md:left-8">
        <div className="bg-sky-500/50 backdrop-blur px-4 py-2 rounded-xl shadow-sm flex items-center justify-center border border-slate-200">
            <EventProLogo className="scale-90 origin-left " />
        </div>
      </div>

      <div className="w-full max-w-md space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700 mt-16 md:mt-0">
        
        {/* LOGO DU CLIENT - CENTRÉ */}
        <div className="flex flex-col items-center justify-center mb-6">
            {companyLogo ? (
                <img src={companyLogo} alt="Logo Client" className="h-20 w-auto object-contain mix-blend-multiply drop-shadow-sm mb-2" />
            ) : (
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm border border-slate-200 mb-2 text-primary">
                    <Zap className="w-8 h-8" />
                </div>
            )}
            
            {companyName !== "Chargement..." && (
                <h1 className="text-2xl font-bold text-slate-900">{companyName}</h1>
            )}
            <p className="text-slate-500 font-bold tracking-widest uppercase text-xs mt-2">Accès Sécurisé</p>
        </div>

        <Card className="border-none shadow-xl bg-white rounded-2xl overflow-hidden">
          <CardContent className="p-8">
            <div className="mb-8 text-center">
                <h2 className="text-2xl font-bold text-slate-900">Bon retour</h2>
                <p className="text-sm text-slate-500 mt-1">Connectez-vous pour accéder à votre espace.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="vous@entreprise.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="bg-slate-50 focus:bg-white border-slate-200 h-12"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-slate-700 font-medium">Mot de passe</Label>
                    <Link to="/forgot-password" size="sm" className="text-sm text-primary hover:underline font-medium">
                        Oublié ?
                    </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="pr-10 bg-slate-50 focus:bg-white border-slate-200 h-12 text-slate-900"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-12 px-3 text-slate-400 hover:text-slate-600 hover:bg-transparent text-slate-900 hover:bg-slate-800"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </Button>
                </div>
              </div>

              <div className="pt-2">
                <Button
                    type="submit"
                    className="w-full h-14 text-lg font-bold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                    disabled={isLoading}
                >
                    {isLoading ? (
                    <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Connexion...
                    </>
                    ) : (
                    'Se connecter'
                    )}
                </Button>
              </div>
            </form>

            <div className="mt-8 text-center border-t border-slate-100 pt-6">
              <p className="text-sm text-slate-500">
                Pas encore de compte ?{' '}
                <Link to="/auth/register" className="text-primary hover:underline font-bold transition-colors">
                  Créer un compte
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-6">
          En vous connectant, vous acceptez nos conditions d'utilisation et de confidentialité.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;